import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
// Api
import { ACCEPT_MATCHUP_INVITE } from 'api/matchup/mutations';
import { GET_STREAM_REDUCED } from 'api/streams/queries';
// Types
import {
  AcceptMatchupInvite,
  AcceptMatchupInviteVariables,
} from 'api/matchup/types/AcceptMatchupInvite';
import {
  GetStreamReduced,
  GetStreamReducedVariables,
} from 'api/streams/types/GetStreamReduced';
// Constants
import { HOME } from 'constants/routes';

// Accepts the invite and resolves its stream page path, falling back to HOME.
export function useAcceptMatchupInvite() {
  const [acceptInvite, { loading }] = useMutation<
    AcceptMatchupInvite,
    AcceptMatchupInviteVariables
  >(ACCEPT_MATCHUP_INVITE);

  // `stream` resolves hidden streams too, unlike `streamsV2`.
  const [getStream] = useLazyQuery<GetStreamReduced, GetStreamReducedVariables>(
    GET_STREAM_REDUCED
  );

  const acceptMatchupInvite = useCallback(
    async (inviteToken: string): Promise<string> => {
      const { data } = await acceptInvite({
        variables: { input: { inviteToken } },
      });
      const streamId = data?.acceptMatchupInvite?.streamId;
      const { data: streamData } = await getStream({
        variables: { input: { id: streamId } },
      });
      const stream = streamData?.stream;

      return stream?.store?.slug && stream?.slug
        ? `/${stream.store.slug}/streams/${stream.slug}`
        : HOME;
    },
    [acceptInvite, getStream]
  );

  return { acceptMatchupInvite, loading };
}
